/** @file Virtualized tree: expand/collapse, multi-select, keyboard navigation, rename (F2 or double-click). */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import './tokens.css';
import './Tree.css';
import { TreeRowItem, type TreeHandlers } from './TreeRowItem';
import { flattenTree, type TreeNode } from './logic/flattenTree';
import { visibleRange } from './logic/visibleRange';
import { useElementHeight } from './useElementHeight';
import { useTreeDrag, type TreeDropHandlers } from './useTreeDrag';
import { useTreeNavigation } from './useTreeNavigation';

/** Props of Tree; the callbacks come from TreeHandlers. */
export interface TreeProps extends TreeHandlers, TreeDropHandlers {
  nodes: readonly TreeNode[];
  /** Ids of the open nodes (controlled). */
  expanded: ReadonlySet<string>;
  /** Ids of the selected rows (controlled). */
  selected: ReadonlySet<string>;
  /** Already-translated accessible name of the tree. */
  label: string;
  /** Already-translated accessible name of the rename input. */
  renameLabel: string;
  /** Id of the row being renamed, or null. */
  renamingId?: string | null;
  /** Row height in px; default 24. */
  rowHeight?: number;
}

const OVERSCAN = 6;
const FALLBACK_HEIGHT = 400;

/** Renders only the rows in view; give the tree a height through CSS (it fills its parent by default). */
export function Tree(props: TreeProps) {
  const { nodes, expanded, selected, label, renameLabel, renamingId = null, rowHeight = 24 } = props;
  const uid = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const viewportHeight = useElementHeight(scrollRef, FALLBACK_HEIGHT);
  const rows = useMemo(() => flattenTree(nodes, expanded), [nodes, expanded]);
  const nav = useTreeNavigation({ rows, selected, rowHeight, viewportHeight, scrollRef, onScrollTop: setScrollTop, onToggle: props.onToggle, onSelect: props.onSelect, onRenameStart: props.onRenameStart });
  const drag = useTreeDrag(props, rowHeight);
  const wasRenaming = useRef(false);
  useEffect(() => {
    const ended = renamingId === null && wasRenaming.current;
    wasRenaming.current = renamingId !== null;
    if (!ended) return undefined;
    // A rename that ended by blur is still in the middle of a focus change to the clicked control: look again after it, and only take focus when nothing else has it.
    const timer = setTimeout(() => { if (document.activeElement === document.body) scrollRef.current?.focus(); }, 0);
    return () => { clearTimeout(timer); };
  }, [renamingId]);
  const { start, end } = visibleRange({ scrollTop, viewportHeight, rowHeight, count: rows.length, overscan: OVERSCAN });
  return (
    <div
      ref={scrollRef}
      role="tree"
      tabIndex={0}
      aria-label={label}
      aria-multiselectable="true"
      aria-activedescendant={nav.activeId === null ? undefined : `${uid}-${nav.activeId}`}
      className="p2-tree"
      onScroll={(e) => { setScrollTop(e.currentTarget.scrollTop); }}
      onKeyDown={nav.onKeyDown}
    >
      <div className="p2-tree-body" style={{ height: rows.length * rowHeight }}>
        {rows.slice(start, end).map((row, i) => (
          <TreeRowItem key={row.id} row={row} domId={`${uid}-${row.id}`} top={(start + i) * rowHeight} height={rowHeight} selected={selected.has(row.id)} active={row.id === nav.activeId} renaming={row.id === renamingId} renameLabel={renameLabel} handlers={props} drag={drag.rowProps(row.id)} dropHint={drag.hint?.id === row.id ? drag.hint.where : null} />
        ))}
      </div>
    </div>
  );
}
